import { useState, useEffect, useRef, useCallback } from 'react';
import { Crown, X, Sparkles, Star, Gift, Zap, Heart, Megaphone, Flame, ChevronLeft, ChevronRight, Volume2, VolumeX, Check, Mic, Brain, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useTierLabels } from '@/context/TierLabelsContext';
import axios from '@/utils/api';

// Use same-origin API path on production custom domains
const API = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';
const FOUNDING_BADGE_DISMISSED_KEY = 'founding_member_badge_dismissed';

const ICONS = {
  sparkles: Sparkles,
  crown: Crown,
  gift: Gift,
  zap: Zap,
  star: Star,
  fire: Flame,
  heart: Heart,
  megaphone: Megaphone,
};

const PromoBannerCarousel = ({ onAction, includeFoundingBadge = true }) => {
  const { user } = useAuth();
  const { labels } = useTierLabels();
  const [campaigns, setCampaigns] = useState([]);
  const [displaySettings, setDisplaySettings] = useState({
    transition_time: 5,
    auto_rotate: true,
    show_navigation: true,
    show_indicators: true,
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dismissedIds, setDismissedIds] = useState([]);
  const [videoMuted, setVideoMuted] = useState(true);
  const [trackedImpressions, setTrackedImpressions] = useState(new Set());
  const [trackedVideoPlays, setTrackedVideoPlays] = useState(new Set());
  const [foundingBadgeDismissed, setFoundingBadgeDismissed] = useState(true);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const videoRef = useRef(null);
  
  const foundingMemberLabel = labels?.founding_member || 'Founding Member';

  // Track promo event (fire-and-forget)
  // SECURITY: Auth handled via httpOnly cookies (axios.defaults.withCredentials = true)
  const trackEvent = useCallback(async (campaignId, eventType, metadata = {}) => {
    try {
      await axios.post(`${API}/api/promo/track`, {
        campaign_id: campaignId,
        event_type: eventType,
        metadata
      });
    } catch (err) {
      // Silent fail - tracking shouldn't break the UI
      console.debug('Tracking event failed:', err);
    }
  }, []);

  // Track impression when campaign is shown
  const trackImpression = useCallback((campaignId) => {
    if (!trackedImpressions.has(campaignId)) {
      setTrackedImpressions(prev => new Set([...prev, campaignId]));
      trackEvent(campaignId, 'impression');
    }
  }, [trackedImpressions, trackEvent]);

  // Track video play
  const trackVideoPlay = useCallback((campaignId) => {
    if (!trackedVideoPlays.has(campaignId)) {
      setTrackedVideoPlays(prev => new Set([...prev, campaignId]));
      trackEvent(campaignId, 'video_play');
    }
  }, [trackedVideoPlays, trackEvent]);

  // Track video complete
  const trackVideoComplete = useCallback((campaignId, duration) => {
    trackEvent(campaignId, 'video_complete', { video_duration: duration });
  }, [trackEvent]);

  // Track click
  const trackClick = useCallback((campaignId) => {
    trackEvent(campaignId, 'click');
  }, [trackEvent]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      // Wait for user data from AuthContext
      if (!user) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // Use user from AuthContext instead of making a separate /auth/me call
        // B2B users get their client's promos, regular users get admin promos
        const endpoint = user?.client_id 
          ? `${API}/api/client-promos/active`
          : `${API}/api/promo/active-all`;
        
        const response = await axios.get(endpoint);
        setCampaigns(response.data.campaigns || []);
        if (response.data.display_settings) {
          setDisplaySettings(response.data.display_settings);
        }
      } catch (error) {
        // Silently fail - campaigns are non-critical
        console.debug('Campaigns fetch failed:', error.message);
      } finally {
        setLoading(false);
      }
    };

    // Defer campaigns - non-critical
    const timer = setTimeout(() => {
      fetchCampaigns();
    }, 1800);
    
    // Load dismissed campaigns from localStorage (non-sensitive UI preference)
    const dismissed = JSON.parse(localStorage.getItem('dismissed_promos') || '[]');
    setDismissedIds(dismissed);
    
    // Load founding badge dismissed state
    const foundingDismissed = localStorage.getItem(FOUNDING_BADGE_DISMISSED_KEY);
    setFoundingBadgeDismissed(foundingDismissed === 'true');
    
    return () => clearTimeout(timer);
  }, [user?.user_id, user?.client_id]); // Re-fetch when user changes (login/logout)

  // Check if we should show founding member badge
  const showFoundingBadge = includeFoundingBadge && 
    user?.membership_tier === 'founding_member' && 
    !foundingBadgeDismissed;

  // Check if we should show premium member badge
  const premiumBadgeDismissed = localStorage.getItem('premium_badge_dismissed') === 'true';
  const showPremiumBadge = includeFoundingBadge && 
    user?.membership_tier === 'premium' && 
    !premiumBadgeDismissed;

  // Get premium label from tier labels
  const premiumLabel = labels?.premium || 'Premium Member';

  // Handle founding badge dismiss - show confirmation dialog
  const handleDismissFoundingBadge = () => {
    setShowDismissConfirm(true);
  };

  // Actually dismiss the founding badge forever
  const confirmDismissForever = () => {
    localStorage.setItem(FOUNDING_BADGE_DISMISSED_KEY, 'true');
    setFoundingBadgeDismissed(true);
    setShowDismissConfirm(false);
  };

  // Dismiss just for this session (will show again on next login)
  const dismissUntilNextLogin = () => {
    setFoundingBadgeDismissed(true);
    setShowDismissConfirm(false);
    // Don't save to localStorage - it will reappear on next login
  };

  // Handle premium badge dismiss
  const handleDismissPremiumBadge = () => {
    localStorage.setItem('premium_badge_dismissed', 'true');
  };

  // Combine campaigns with founding member badge and premium badge
  const allSlides = [
    // Add founding member badge as first slide if applicable
    ...(showFoundingBadge ? [{
      id: 'founding_member_badge',
      type: 'founding_member',
      title: foundingMemberLabel,
    }] : []),
    // Add premium member badge if applicable
    ...(showPremiumBadge ? [{
      id: 'premium_member_badge',
      type: 'premium_member',
      title: premiumLabel,
    }] : []),
    // Add promo campaigns
    ...campaigns.filter(c => !dismissedIds.includes(c.id))
  ];

  // Auto-rotate with proper cleanup and mobile support
  useEffect(() => {
    if (!displaySettings.auto_rotate || allSlides.length <= 1) return;
    
    const interval = setInterval(() => {
      // Use functional update to avoid stale closure issues
      setIsTransitioning(true);
      
      // Use requestAnimationFrame for smoother transitions on mobile
      requestAnimationFrame(() => {
        setTimeout(() => {
          setCurrentIndex(prev => (prev + 1) % allSlides.length);
          setIsTransitioning(false);
        }, 300);
      });
    }, displaySettings.transition_time * 1000);
    
    return () => clearInterval(interval);
  }, [displaySettings.auto_rotate, displaySettings.transition_time, allSlides.length]);

  const handleNext = () => {
    if (allSlides.length <= 1) return;
    setIsTransitioning(true);
    requestAnimationFrame(() => {
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % allSlides.length);
        setIsTransitioning(false);
      }, 300);
    });
  };

  const handlePrev = () => {
    if (allSlides.length <= 1) return;
    setIsTransitioning(true);
    requestAnimationFrame(() => {
      setTimeout(() => {
        setCurrentIndex(prev => (prev - 1 + allSlides.length) % allSlides.length);
        setIsTransitioning(false);
      }, 300);
    });
  };

  const handleDismiss = (slideId) => {
    if (slideId === 'founding_member_badge') {
      handleDismissFoundingBadge();
    } else {
      const newDismissed = [...dismissedIds, slideId];
      setDismissedIds(newDismissed);
      localStorage.setItem('dismissed_promos', JSON.stringify(newDismissed));
    }
    
    // Adjust index if needed
    if (currentIndex >= allSlides.length - 1) {
      setCurrentIndex(Math.max(0, allSlides.length - 2));
    }
  };

  const handleCTA = (campaign) => {
    // Track the click
    if (campaign.id !== 'founding_member_badge') {
      trackClick(campaign.id);
    }
    
    // Normalize URL - add https:// if no protocol specified
    const normalizeUrl = (url) => {
      if (!url) return null;
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      if (url.startsWith('/')) return url;
      return `https://${url}`;
    };
    
    if (campaign.cta_action === 'link' && campaign.cta_link) {
      window.open(normalizeUrl(campaign.cta_link), '_blank');
    } else if (campaign.cta_action === 'upgrade') {
      onAction?.('upgrade', campaign);
    } else if (campaign.cta_action === 'dismiss') {
      handleDismiss(campaign.id);
    } else {
      onAction?.('custom', campaign);
    }
  };

  // Don't show if loading or no slides
  if (loading || allSlides.length === 0) {
    return null;
  }

  const currentSlide = allSlides[currentIndex];
  if (!currentSlide) return null;

  // Render Founding Member Badge slide - Original beautiful design restored
  const renderFoundingMemberSlide = () => {
    const perks = [
      { icon: Sparkles, text: 'Unlimited journal entries forever' },
      { icon: Zap, text: 'Unlimited voice typing & AI Coach' },
      { icon: Gift, text: 'All future premium features included' },
      { icon: Heart, text: 'Priority support & early access' },
    ];

    return (
      <div 
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(245, 158, 11, 0.08) 50%, rgba(217, 119, 6, 0.08) 100%)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
        }}
        data-testid="founding-member-slide"
      >
        {/* Animated golden particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-3 left-12 w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse opacity-70" />
          <div className="absolute top-6 right-24 w-2 h-2 bg-amber-400 rounded-full animate-pulse opacity-50" style={{ animationDelay: '0.3s' }} />
          <div className="absolute bottom-4 left-1/3 w-1 h-1 bg-yellow-300 rounded-full animate-pulse opacity-60" style={{ animationDelay: '0.7s' }} />
          <Star className="absolute top-2 right-8 w-3 h-3 text-yellow-400/30 animate-pulse" style={{ animationDelay: '0.5s' }} />
        </div>

        <div className="relative p-5">
          {/* Desktop: Horizontal layout | Mobile: Compact vertical */}
          <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-6">
            {/* Left: Icon & Title */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 50%, #D97706 100%)',
                  boxShadow: '0 4px 20px rgba(251, 191, 36, 0.4)',
                }}
              >
                <Crown className="w-7 h-7 text-white drop-shadow-lg" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold" style={{ 
                    background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    {foundingMemberLabel}
                  </h3>
                  <span className="text-base">✨</span>
                </div>
                <p className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
                  Thank you for being an early adopter!
                </p>
              </div>
            </div>

            {/* Center: Perks (hidden on mobile for compact look, shown on desktop) */}
            <div className="hidden lg:flex flex-wrap justify-center gap-2 flex-1">
              {perks.map((perk) => (
                <div 
                  key={perk.text}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm"
                  style={{ 
                    background: 'rgba(251, 191, 36, 0.1)',
                    border: '1px solid rgba(251, 191, 36, 0.2)',
                    color: 'var(--theme-text)',
                  }}
                >
                  <perk.icon className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                  <span>{perk.text}</span>
                </div>
              ))}
            </div>

            {/* Right: Lifetime Access Badge */}
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <div 
                className="px-4 py-2 rounded-xl font-semibold text-sm"
                style={{
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 163, 74, 0.2) 100%)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  color: '#22C55E',
                }}
              >
                <Check className="w-4 h-4 inline mr-1" />
                Lifetime Access
              </div>
              <p className="text-xs" style={{ color: 'var(--theme-text-muted)' }}>
                You'll never pay for premium
              </p>
            </div>
          </div>

          {/* Mobile-only: Show perks as a simple list below */}
          <div className="lg:hidden mt-4 grid grid-cols-2 gap-2">
            {perks.map((perk) => (
              <div 
                key={perk.text}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs"
                style={{ 
                  background: 'rgba(251, 191, 36, 0.1)',
                  color: 'var(--theme-text)',
                }}
              >
                <perk.icon className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                <span className="truncate">{perk.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render Premium Member Badge slide
  const renderPremiumMemberSlide = () => {
    const perks = [
      { icon: Sparkles, text: 'Full journaling access' },
      { icon: Zap, text: 'Voice typing & AI Coach' },
      { icon: Gift, text: 'Premium features unlocked' },
      { icon: Heart, text: 'Thank you for subscribing!' },
    ];

    return (
      <div 
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(168, 85, 247, 0.08) 50%, rgba(192, 132, 252, 0.08) 100%)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
        }}
        data-testid="premium-member-slide"
      >
        {/* Animated purple particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-3 left-12 w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse opacity-70" />
          <div className="absolute top-6 right-24 w-2 h-2 bg-violet-400 rounded-full animate-pulse opacity-50" style={{ animationDelay: '0.3s' }} />
          <div className="absolute bottom-4 left-1/3 w-1 h-1 bg-purple-300 rounded-full animate-pulse opacity-60" style={{ animationDelay: '0.7s' }} />
          <Star className="absolute top-2 right-8 w-3 h-3 text-purple-400/30 animate-pulse" style={{ animationDelay: '0.5s' }} />
        </div>

        <div className="relative p-5">
          <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-6">
            {/* Left: Icon & Title */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #A855F7 50%, #C084FC 100%)',
                  boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4)',
                }}
              >
                <Star className="w-7 h-7 text-white drop-shadow-lg" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold" style={{ 
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    {premiumLabel}
                  </h3>
                  <span className="text-base">⭐</span>
                </div>
                <p className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
                  You have full premium access!
                </p>
              </div>
            </div>

            {/* Center: Perks (hidden on mobile) */}
            <div className="hidden lg:flex flex-wrap justify-center gap-2 flex-1">
              {perks.map((perk) => (
                <div 
                  key={perk.text}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm"
                  style={{ 
                    background: 'rgba(139, 92, 246, 0.1)',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    color: 'var(--theme-text)',
                  }}
                >
                  <perk.icon className="w-3.5 h-3.5 text-purple-400" />
                  <span>{perk.text}</span>
                </div>
              ))}
            </div>

            {/* Right: Manage Subscription Button */}
            <Button
              onClick={() => onAction?.('manage_subscription', { id: 'premium_member_badge' })}
              className="flex-shrink-0 font-medium text-sm hidden lg:flex"
              style={{
                background: 'rgba(139, 92, 246, 0.2)',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                color: '#A855F7',
              }}
              data-testid="manage-subscription-desktop-btn"
            >
              Manage Subscription
            </Button>
          </div>

          {/* Mobile: Show perks in a scrollable row */}
          <div className="flex lg:hidden gap-2 mt-3 overflow-x-auto pb-1 -mx-1 px-1">
            {perks.map((perk) => (
              <div 
                key={perk.text}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-xs whitespace-nowrap flex-shrink-0"
                style={{ 
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  color: 'var(--theme-text)',
                }}
              >
                <perk.icon className="w-3 h-3 text-purple-400" />
                <span>{perk.text}</span>
              </div>
            ))}
          </div>
          
          {/* Mobile: Manage Subscription Button */}
          <Button
            onClick={() => onAction?.('manage_subscription', { id: 'premium_member_badge' })}
            className="lg:hidden w-full mt-3 font-medium text-sm py-3"
            style={{
              background: 'rgba(139, 92, 246, 0.2)',
              border: '1px solid rgba(139, 92, 246, 0.4)',
              color: '#A855F7',
            }}
            data-testid="manage-subscription-mobile-btn"
          >
            Manage Subscription
          </Button>
        </div>
      </div>
    );
  };

  const renderCampaign = (campaign) => {
    // Track impression when campaign is rendered
    if (campaign.id !== 'founding_member_badge') {
      trackImpression(campaign.id);
    }
    
    // Video banner
    if (campaign.banner_type === 'video' && campaign.video_url) {
      return (
        <div 
          className="relative overflow-hidden rounded-2xl"
          style={{ 
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            height: '200px',
          }}
          data-testid="promo-video-banner"
        >
          <video
            ref={videoRef}
            src={campaign.video_url}
            className="w-full h-full object-cover absolute inset-0"
            autoPlay
            loop
            muted={videoMuted}
            playsInline
            onPlay={() => trackVideoPlay(campaign.id)}
            onEnded={(e) => trackVideoComplete(campaign.id, e.target.duration)}
            onError={(e) => console.error('Video load error:', e)}
          />
          {/* Title overlay */}
          <div className="absolute top-4 left-4 z-10">
            <h3 className="text-white font-bold text-lg drop-shadow-lg">{campaign.title}</h3>
            {campaign.subtitle && (
              <p className="text-white/80 text-sm drop-shadow-md">{campaign.subtitle}</p>
            )}
          </div>
          {/* Video controls overlay */}
          <div className="absolute bottom-3 right-3 flex gap-2 z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setVideoMuted(!videoMuted);
              }}
              className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            >
              {videoMuted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
            </button>
          </div>
          {/* CTA overlay for video */}
          {campaign.cta_text && (
            <div className="absolute bottom-3 left-3 z-10">
              <Button
                onClick={() => handleCTA(campaign)}
                size="sm"
                className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white shadow-lg"
              >
                {campaign.cta_text}
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Image banner
    if (campaign.banner_type === 'image' && campaign.image_url) {
      return (
        <div 
          className="relative overflow-hidden rounded-2xl cursor-pointer group"
          style={{ height: '200px' }}
          onClick={() => handleCTA(campaign)}
          data-testid="promo-image-banner"
        >
          <img 
            src={campaign.image_url} 
            alt={campaign.title || 'Promotional banner'}
            className="w-full h-full object-cover absolute inset-0 transition-transform group-hover:scale-[1.02]"
          />
          {/* CTA overlay for image */}
          {campaign.cta_text && (
            <div className="absolute bottom-4 left-4 z-10">
              <Button
                onClick={(e) => { e.stopPropagation(); handleCTA(campaign); }}
                size="sm"
                className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white shadow-lg"
              >
                {campaign.cta_text}
              </Button>
            </div>
          )}
          {/* Title overlay */}
          {campaign.title && (
            <div className="absolute top-4 left-4 z-10">
              <h3 className="text-white font-bold text-lg drop-shadow-lg">{campaign.title}</h3>
              {campaign.subtitle && (
                <p className="text-white/80 text-sm drop-shadow-md">{campaign.subtitle}</p>
              )}
            </div>
          )}
        </div>
      );
    }

    // Styled banner (default)
    const IconComponent = ICONS[campaign.icon] || Sparkles;
    const gradientFrom = campaign.gradient_from || '#df1283';
    const gradientTo = campaign.gradient_to || '#FF7901';

    return (
      <div 
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: `linear-gradient(135deg, ${gradientFrom}20 0%, ${gradientTo}20 100%)`,
          border: `1px solid ${gradientFrom}50`,
        }}
        data-testid="promo-styled-banner"
      >
        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-2 left-10 w-2 h-2 rounded-full animate-pulse opacity-60" style={{ background: gradientFrom }} />
          <div className="absolute top-8 right-20 w-1.5 h-1.5 rounded-full animate-pulse opacity-50" style={{ background: gradientTo, animationDelay: '0.5s' }} />
          <div className="absolute bottom-4 left-1/4 w-2 h-2 rounded-full animate-pulse opacity-40" style={{ background: gradientFrom, animationDelay: '1s' }} />
        </div>

        <div className="relative p-5 sm:p-6">
          <div className="flex flex-col lg:flex-row items-center gap-5 lg:gap-8">
            {/* Icon & Title */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
                  boxShadow: `0 4px 20px ${gradientFrom}60`,
                }}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--theme-text)' }}>
                    {campaign.title}
                  </h3>
                  {campaign.discount_label && (
                    <span 
                      className="px-2 py-0.5 text-xs font-bold rounded-full text-white"
                      style={{ background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)` }}
                    >
                      {campaign.discount_label}
                    </span>
                  )}
                </div>
                {campaign.subtitle && (
                  <p className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
                    {campaign.subtitle}
                  </p>
                )}
              </div>
            </div>

            {/* Description */}
            {campaign.description && (
              <div className="flex-1 text-center lg:text-left">
                <p className="text-sm" style={{ color: 'var(--theme-text)' }}>
                  {campaign.description}
                </p>
              </div>
            )}

            {/* CTA */}
            <div className="flex-shrink-0">
              <Button
                onClick={() => handleCTA(campaign)}
                className="px-6 py-2.5 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
                  color: 'white',
                }}
                data-testid="promo-cta-btn"
              >
                {campaign.cta_text || 'Learn More'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div 
      className="relative mb-6" 
      data-testid="promo-carousel"
    >
      {/* Navigation arrows */}
      {displaySettings.show_navigation && allSlides.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </>
      )}

      {/* Dismiss button - only show if promo allows dismiss AND user's tier can dismiss */}
      {(() => {
        // Check if dismiss is allowed for this slide
        const allowDismiss = currentSlide.allow_dismiss !== false; // Default to true if not set
        const dismissAllowedTiers = currentSlide.dismiss_allowed_tiers || ['free', 'premium', 'founding_member', 'enterprise'];
        const userTier = user?.membership_tier || 'free';
        const userCanDismiss = allowDismiss && dismissAllowedTiers.includes(userTier);
        
        if (!userCanDismiss) return null;
        
        return (
          <button
            onClick={() => handleDismiss(currentSlide.id)}
            className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
            data-testid="dismiss-promo"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        );
      })()}

      {/* Sponsored Label */}
      {currentSlide.is_sponsored && (
        <div className="absolute top-3 left-3 z-10 px-2 py-1 bg-black/50 rounded-full">
          <span className="text-xs text-white/80 font-medium">
            {currentSlide.sponsor_name || 'Sponsored'}
          </span>
        </div>
      )}

      {/* Slide content - use transform for GPU acceleration on mobile */}
      <div 
        className="transition-opacity duration-300 will-change-[opacity]"
        style={{ 
          opacity: isTransitioning ? 0 : 1,
          transform: 'translateZ(0)' // Force GPU layer for smoother transitions
        }}
      >
        {currentSlide.type === 'founding_member' 
          ? renderFoundingMemberSlide() 
          : currentSlide.type === 'premium_member'
            ? renderPremiumMemberSlide()
            : renderCampaign(currentSlide)
        }
      </div>

      {/* Dot indicators */}
      {displaySettings.show_indicators && allSlides.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {allSlides.map((slide, idx) => (
            <button
              key={slide.id || `slide-${idx}`}
              onClick={() => {
                if (idx === currentIndex) return; // Don't transition to same slide
                setIsTransitioning(true);
                requestAnimationFrame(() => {
                  setTimeout(() => {
                    setCurrentIndex(idx);
                    setIsTransitioning(false);
                  }, 300);
                });
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex 
                  ? 'bg-white w-4' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      )}

      {/* Founding Member Dismiss Confirmation Dialog */}
      {showDismissConfirm && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={() => setShowDismissConfirm(false)}
        >
          <div 
            className="relative max-w-md w-full rounded-2xl p-6 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(0, 0, 0, 0.95) 30%)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="founding-dismiss-dialog"
          >
            {/* Golden accent line at top */}
            <div 
              className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
              style={{ background: 'linear-gradient(90deg, #FCD34D, #F59E0B, #D97706)' }}
            />
            
            {/* Crown icon */}
            <div className="flex justify-center mb-4">
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 50%, #D97706 100%)',
                  boxShadow: '0 4px 20px rgba(251, 191, 36, 0.4)',
                }}
              >
                <Crown className="w-8 h-8 text-white" />
              </div>
            </div>
            
            <h3 
              className="text-xl font-bold text-center mb-2"
              style={{ 
                background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Hide Founding Member Banner?
            </h3>
            
            <p className="text-center text-white/70 text-sm mb-6">
              You can hide this banner temporarily or permanently. Don't worry, your Founding Member status stays the same either way!
            </p>
            
            <div className="space-y-3">
              <button
                onClick={dismissUntilNextLogin}
                className="w-full py-3 px-4 rounded-xl font-medium transition-all text-white"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
                data-testid="founding-dismiss-session"
              >
                Hide until next login
              </button>
              
              <button
                onClick={confirmDismissForever}
                className="w-full py-3 px-4 rounded-xl font-medium transition-all"
                style={{
                  background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                  color: '#000',
                }}
                data-testid="founding-dismiss-forever"
              >
                Hide forever
              </button>
              
              <button
                onClick={() => setShowDismissConfirm(false)}
                className="w-full py-2 text-white/50 hover:text-white text-sm transition-colors"
                data-testid="founding-dismiss-cancel"
              >
                Keep showing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoBannerCarousel;
